import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/data/products";
import { LOYALTY } from "@/data/site";

const STATUS_LABEL = {
  created: "Awaiting Payment",
  paid: "Paid",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  failed: "Failed",
};

const emptyAddress = { label: "Home", fullName: "", phone: "", line1: "", line2: "", city: "", state: "", postalCode: "", country: "India" };

const Account = () => {
  const { user, logout, refresh } = useAuth();
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [address, setAddress] = useState(emptyAddress);
  const [saving, setSaving] = useState(false);

  const { data: ordersData } = useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => (await api.get("/orders/mine")).data,
  });

  const saveAddress = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users/me/addresses", address);
      await refresh();
      setAddress(emptyAddress);
      setShowAddressForm(false);
      toast.success("Address saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save address");
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (id) => {
    try {
      await api.delete(`/users/me/addresses/${id}`);
      await refresh();
      toast.success("Address removed");
    } catch {
      toast.error("Could not remove address");
    }
  };

  if (!user) return null;

  return (
    <section className="max-w-4xl mx-auto px-6 py-32">
      <div className="flex items-center justify-between mb-16">
        <div>
          <p className="font-serif-lux italic font-light text-4xl mb-2">{user.name}</p>
          <p className="text-xs font-light text-[#5C5C5C]">{user.email}</p>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#5C5C5C] hover:text-[#111111] transition-colors duration-300"
        >
          <LogOut size={14} strokeWidth={1.5} /> Log Out
        </button>
      </div>

      <Link
        to="/loyalty"
        data-testid="account-loyalty-card"
        className="flex items-center justify-between border border-[#EBE8E3] bg-white px-8 py-6 mb-16 hover:border-[#C07C5F] transition-colors duration-300"
      >
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-1">My Loyalty Points</p>
          <p className="font-serif-lux text-4xl" data-testid="account-loyalty-points">{user.loyaltyPoints || 0}</p>
        </div>
        <p className="text-[11px] tracking-[0.25em] uppercase text-[#C07C5F]">
          Worth {formatINR((user.loyaltyPoints || 0) * LOYALTY.pointValue)}
        </p>
      </Link>

      <div className="grid md:grid-cols-2 gap-16">
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-[11px] tracking-[0.35em] uppercase text-[#5C5C5C]">Saved Addresses</h2>
            <button
              onClick={() => setShowAddressForm((s) => !s)}
              data-testid="add-address-toggle"
              className="text-[#C07C5F] hover:text-[#111111] transition-colors duration-300"
            >
              <Plus size={18} />
            </button>
          </div>

          {showAddressForm && (
            <form onSubmit={saveAddress} className="border border-[#EBE8E3] p-6 mb-6 flex flex-col gap-4" data-testid="address-form">
              <input required placeholder="Full name" value={address.fullName} onChange={(e) => setAddress({ ...address, fullName: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <input required placeholder="Phone" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <input required placeholder="Address line 1" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <input placeholder="Address line 2 (optional)" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="City" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
                <input required placeholder="State" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              </div>
              <input required placeholder="Postal code" value={address.postalCode} onChange={(e) => setAddress({ ...address, postalCode: e.target.value })} className="border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm" />
              <button type="submit" disabled={saving} data-testid="save-address-btn" className="mt-2 bg-[#111111] text-[#F9F8F6] rounded-full py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500 disabled:opacity-50">
                {saving ? "Saving…" : "Save Address"}
              </button>
            </form>
          )}

          <div className="flex flex-col gap-4">
            {(user.addresses || []).length === 0 && !showAddressForm && (
              <p className="text-xs font-light text-[#5C5C5C]">No addresses saved yet.</p>
            )}
            {(user.addresses || []).map((a) => (
              <div key={a._id} className="border border-[#EBE8E3] p-5 flex justify-between gap-4" data-testid={`address-${a._id}`}>
                <div className="text-sm font-light">
                  <p className="font-medium">{a.fullName} {a.isDefault && <span className="text-[10px] text-[#C07C5F] uppercase tracking-wide ml-1">Default</span>}</p>
                  <p className="text-[#5C5C5C]">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                  <p className="text-[#5C5C5C]">{a.city}, {a.state} {a.postalCode}</p>
                  <p className="text-[#5C5C5C]">{a.phone}</p>
                </div>
                <button onClick={() => deleteAddress(a._id)} data-testid={`delete-address-${a._id}`} className="text-[#5C5C5C] hover:text-[#111111] transition-colors duration-300 h-fit">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-[11px] tracking-[0.35em] uppercase text-[#5C5C5C] mb-6">Order History</h2>
          <div className="flex flex-col gap-4">
            {(ordersData?.orders || []).length === 0 && (
              <p className="text-xs font-light text-[#5C5C5C]">No orders yet.</p>
            )}
            {(ordersData?.orders || []).map((o) => (
              <Link
                to={`/order/${o._id}`}
                key={o._id}
                data-testid={`order-row-${o.orderNumber}`}
                className="border border-[#EBE8E3] p-5 flex justify-between items-center hover:border-[#C07C5F] transition-colors duration-300"
              >
                <div>
                  <p className="font-serif-lux text-lg">{o.orderNumber}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C] mt-1">{STATUS_LABEL[o.status] || o.status}</p>
                  {o.shipping?.awb_code && (
                    <p className="text-[10px] text-[#C07C5F] mt-1">{o.shipping.courier_name} · {o.shipping.awb_code}</p>
                  )}
                </div>
                <p className="font-serif-lux text-lg">{formatINR(o.total)}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Account;
